import React from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Spinner } from './ui/spinner';
import { Alert, AlertDescription } from './ui/alert';
import { AlertCircle, Calendar, Pencil, User, Users } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useMyFamilies } from '../hooks/useMyFamilies';
import { formatFamilyName } from '../utils/family.utils';
import { UserStatus } from '../types/enums';
// import { toast } from 'sonner';

// ... imports

const getFamilyStatusBadge = (status: UserStatus) => {
  switch (status) {
    case UserStatus.ACTIVE:
      return { variant: 'success' as const, label: 'Active' };
    case UserStatus.INACTIVE:
      return { variant: 'default' as const, label: 'Inactive' };
    case UserStatus.PENDING:
      return { variant: 'warning' as const, label: 'Pending' };
    default:
      return null;
  }
};

export const FamilySelector: React.FC = () => {
  const { t } = useTranslation('common');
  const { loading, error, refetch, user, families } = useMyFamilies();
  const navigate = useNavigate();
  const location = useLocation();

  const selectedEvent = location.state?.selectedEvent;
  const isEventFirstFlow = !!selectedEvent;

  const handleFamilyClick = (familyId: string) => {
    if (isEventFirstFlow) {
      navigate(`/family/${familyId}`, {
        state: {
          selectedEvent,
          returnTo: '/events',
        },
      });
    }
  };

  const handleEditFamily = (familyId: string) => {
    navigate(`/family/${familyId}?edit=true`);
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            {t('families.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <Spinner className="h-8 w-8 mx-auto mb-4" />
              <p className="text-sm text-gray-600">{t('families.loadingFamilies')}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            {t('families.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert className="border-red-200 bg-red-50">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">
              {error}
              <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-2 w-full">
                {t('buttons.back')}
              </Button>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          {t('families.title')}
        </CardTitle>
        {isEventFirstFlow && selectedEvent && (
          <Alert className="mt-3 border-[var(--color-secondary-300)] bg-[var(--color-secondary-100)]">
            <Calendar className="h-4 w-4" />
            <AlertDescription>
              <div className="flex flex-col gap-2">
                <p className="font-medium text-[var(--color-neutral-900)]">
                  {t('events.selectedEvent')}:<span className="block">{selectedEvent.name}</span>
                </p>
              </div>
            </AlertDescription>
          </Alert>
        )}
        {user && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <User className="h-4 w-4" />
            {user.firstName} {user.lastName} ({user.email})
          </div>
        )}
      </CardHeader>
      <CardContent>
        {families.length === 0 ? (
          <div className="text-center py-8">
            <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-600 mb-2">{t('families.noFamilies')}</p>
            <p className="text-sm text-gray-500">{t('families.addFamily')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-gray-600 mb-4">
              {isEventFirstFlow
                ? t('families.selectFamilyPrompt')
                : t('families.accessCount', { count: families.length })}
            </p>
            {families.map((family) => {
              const isPending = family.status === UserStatus.PENDING;

              return (
                <div
                  key={family.id}
                  className={`p-3 border border-gray-200 rounded-lg ${
                    isEventFirstFlow
                      ? isPending
                        ? 'opacity-60 bg-gray-50 cursor-not-allowed'
                        : 'hover:bg-gray-50 transition-colors cursor-pointer'
                      : ''
                  }`}
                  onClick={
                    isEventFirstFlow
                      ? () => {
                          if (isPending) {
                            // toast.error("Registration is not allowed while the family application is pending.");
                            alert(t('families.pendingRegistrationError'));
                            return;
                          }
                          handleFamilyClick(family.id);
                        }
                      : undefined
                  }
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 flex flex-wrap items-center gap-2 break-words">
                        {formatFamilyName(family.name, t)}
                        {(() => {
                          const badgeConfig = getFamilyStatusBadge(family.status);
                          if (!badgeConfig) return null;
                          return (
                            <Badge variant={badgeConfig.variant} className="ml-0 sm:ml-2">
                              {t(`familyStatus.${badgeConfig.label.toLowerCase()}`)}
                            </Badge>
                          );
                        })()}
                      </h4>
                    </div>
                    {isEventFirstFlow ? (
                      <div className="text-gray-400 self-end sm:self-center">
                        <Users className="h-4 w-4" />
                      </div>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditFamily(family.id);
                        }}
                        className="text-gray-600 hover:text-blue-600 w-full sm:w-auto mt-2 sm:mt-0"
                      >
                        <Pencil className="h-4 w-4 mr-1" />
                        {t('buttons.edit')}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
